export interface BlogArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  authorAvatar?: string;
  publishDate: string;
  readTime: number; // in minutes
  category: string;
  tags: string[];
  image: string;
  featured?: boolean;
  relatedColors?: string[];
  order?: number;
}

export const blogArticles: BlogArticle[] = [
  //   {
  //     id: 'how-lighting-changes-your-palette',
  //     title: 'How Lighting Changes Your Palette',
  //     excerpt:
  //       'Discover how different lighting conditions can dramatically affect how colours appear on you and learn to make informed colour choices.',
  //     content: `# How Lighting Changes Your Palette

  // Lighting is one of the most overlooked factors in colour analysis, yet it has a profound impact on how colours appear on your skin tone. Understanding this relationship can transform how you approach colour selection.

  // ## The Science Behind Lighting and Colour

  // Different types of lighting have different colour temperatures:

  // ### Natural Daylight
  // Natural daylight is considered the gold standard for colour analysis. It provides the most accurate representation of how colours look on your skin. This is why professional colour consultations are often done near windows or in well-lit outdoor spaces.

  // **Best for:**
  // - True colour evaluation
  // - Initial colour analysis
  // - Professional consultations

  // ### Indoor Lighting
  // Most indoor lighting has a colour temperature that can skew how colours appear:

  // - **Incandescent bulbs**: Warm, yellow-toned light that makes warm colours pop but can make cool colours appear dull
  // - **Fluorescent lights**: Cool, blue-toned light that can make warm colours appear washed out
  // - **LED lights**: Varies widely but often has a cool undertone

  // **Impact:**
  // - Colours that look great in store lighting might look completely different in natural light
  // - Your "perfect" shirt might not look as good in office lighting
  // - This is why "buyer's remorse" happens with colour choices

  // ### Evening and Artificial Lighting
  // Evening events and artificial lighting can dramatically change colour perception:

  // - **Candlelight**: Very warm, makes cool tones appear muted
  // - **Restaurant lighting**: Often dim and warm, changing how colours appear
  // - **Nightclub lighting**: Can completely distort colour perception

  // ## Practical Tips

  // 1. **Always check colours in natural light** before making purchases
  // 2. **Consider where you'll wear colours** - office, evening events, or casual daytime
  // 3. **Test colours in your intended lighting** - if you work in an office, check colours there too
  // 4. **Create a lighting reference** - note which colours look best in your most common environments

  // ## Seasonal Considerations

  // Different seasons bring different natural light qualities:

  // - **Spring/Summer**: Bright, clear light - colours appear vibrant
  // - **Fall/Winter**: Softer, diffused light - colours appear muted

  // Your colour palette should account for these seasonal lighting changes.

  // ## Conclusion

  // Understanding lighting is essential for making informed colour choices. The colours in your palette are your foundation, but how they appear depends on the lighting you're in. Take lighting into account when building your wardrobe and choosing colours for specific occasions.`,
  //     author: 'Sarah Chen',
  //     publishDate: '2024-01-15',
  //     readTime: 6,
  //     category: 'Colour Theory',
  //     tags: ['lighting', 'color-analysis', 'wardrobe', 'tips'],
  //     image:
  //       'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop',
  //     featured: true,
  //     relatedColors: ['#FFD700', '#FFA500', '#FF6B35'],
  //   },
  //   {
  //     id: 'color-combos-warm-undertones',
  //     title: '5 Colour Combos for Warm Undertones',
  //     excerpt:
  //       'Discover perfect colour combinations that flatter warm undertones and create harmonious outfits.',
  //     content: `# 5 Colour Combos for Warm Undertones

  // If you have warm undertones, your skin has golden, peachy, or yellow tones. These colour combinations will make you glow!

  // ## Understanding Warm Undertones

  // Warm undertones typically mean:
  // - Gold jewelry looks better than silver
  // - Veins appear greenish
  // - Skin has golden, peachy, or yellow tones
  // - You tan easily and rarely burn
  // - You suit colours like coral, peach, and gold

  // ## Colour Combination #1: Earth and Sky
  // **Colours:** Warm brown + Soft peach + Cream

  // This combination creates a grounded, elegant look. The warm brown provides depth, while the soft peach adds warmth to your complexion. Cream ties everything together for a sophisticated finish.

  // **Best for:** Business casual, daytime events, autumn wardrobe

  // ## Colour Combination #2: Golden Hour
  // **Colours:** Burnt orange + Mustard yellow + Beige

  // This trio is perfect for warm undertones. The burnt orange brings out golden tones in your skin, while mustard yellow adds vibrancy. Beige provides a neutral base that lets the warm colours shine.

  // **Best for:** Weekend wear, casual outings, summer evenings

  // ## Colour Combination #3: Coral Coast
  // **Colours:** Coral + Warm navy + Khaki

  // Coral is the secret weapon for warm undertones. Paired with warm navy (not cool navy) and khaki, you get a fresh, sophisticated palette that works year-round.

  // **Best for:** Spring and summer, vacation wear, date nights

  // ## Colour Combination #4: Autumn Spice
  // **Colours:** Warm burgundy + Olive green + Camel

  // Rich and earthy, this combination is perfect for cooler months. The warm burgundy enhances your natural warmth, while olive green provides beautiful contrast. Camel adds sophistication.

  // **Best for:** Fall and winter, professional settings, evening wear

  // ## Colour Combination #5: Sunset Glow
  // **Colours:** Terracotta + Warm pink + Ivory

  // Soft yet vibrant, this combination is universally flattering for warm undertones. Terracotta brings out your golden tones, warm pink adds femininity, and ivory keeps it fresh.

  // **Best for:** All occasions, versatile year-round option

  // ## Tips for Mixing Colours

  // 1. **Always include one neutral** in your combination
  // 2. **Stick to warm versions** of cool colours (warm navy vs cool navy)
  // 3. **Balance intensity** - pair bold with soft
  // 4. **Consider contrast** - light warm tones with deep warm tones

  // ## Common Mistakes to Avoid

  // - Mixing warm and cool tones in one outfit
  // - Choosing colours that are too bright or too muted
  // - Ignoring the neutral base colour

  // ## Conclusion

  // These colour combinations are your starting point. Experiment, take photos, and see what makes you feel most confident. Remember, these are guidelines - personal preference and confidence matter most!`,
  //     author: 'Marcus Rodriguez',
  //     publishDate: '2024-01-10',
  //     readTime: 8,
  //     category: 'Style Guide',
  //     tags: ['warm-undertones', 'color-combinations', 'style', 'wardrobe'],
  //     image:
  //       'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&h=400&fit=crop',
  //     featured: true,
  //     relatedColors: ['#FF6B35', '#FFA500', '#8B4513'],
  //   },
  {
    id: 'understanding-your-neutrals',
    title: 'Understanding Your Neutrals',
    excerpt:
      "Neutrals aren't just black and white. Discover your perfect neutral palette based on your colour analysis.",
    content: `# Understanding Your Neutrals

Neutrals are the foundation of every great wardrobe. But did you know that not all neutrals work for everyone? Understanding which neutrals flatter your specific colouring is crucial.

## What Are Neutrals?

Neutrals are colours that don't compete with your skin tone. They include:
**• Black and white**
**• Grays (warm and cool)**
**• Browns and tans**
**• Navy and charcoal**
**• Beiges and creams**

## Why Neutrals Matter

Neutrals serve multiple purposes:
**1. Foundation pieces:** Build a versatile wardrobe
**2. Colour balancing:** Tone down or enhance other colours
**3. Professional wear:** Essential for business attire
**4. Investment pieces:** Timeless and long-lasting

## Warm Undertones: Your Neutrals

If you have warm undertones, these neutrals work best:

### Primary Neutrals
**• Warm brown** (camel, chocolate)
**• Warm beige** (sand, cream)
**• Warm gray** (taupe, charcoal with warmth)
**• Warm navy** (not cool navy)
**• Ivory** (rather than pure white)
**• Olive** (as a neutral for warm tones)

### Colours to Avoid
**• Pure black (too harsh)**
**• Cool gray (washes you out)**
**• Pure white (too stark)**
**• Cool navy (doesn't harmonize)**

## Cool Undertones: Your Neutrals

If you have cool undertones, these neutrals work best:

### Primary Neutrals
**• True black** (crisp and clean)
**• Cool gray** (slate, silver-gray)
**• Cool navy** (rich and deep)
**• Pure white** (bright and clear)
**• Charcoal** (blue-based gray)
**• Cool brown** (ash brown)

### Colours to Avoid
**• Warm brown (makes you look sallow)**
**• Beige (too yellow)**
**• Warm gray (muddies your colouring)**
**• Ivory (too yellow)**

## Neutral Combinations

### The Classic Trio
Every wardrobe needs three core neutrals:
1. A dark neutral (for depth)
2. A medium neutral (for versatility)
3. A light neutral (for freshness)

### Building a Neutral Wardrobe

**Start with:**
- 2-3 dark neutrals (pants, jackets)
- 2-3 light neutrals (tops, blouses)
- 1-2 medium neutrals (blazers, sweaters)

## Mixing Neutrals

You can mix neutrals beautifully! Here's how:

1. **Vary the tones** - Mix light, medium, and dark
2. **Add texture** - Different fabrics create interest
3. **Use accessories** - Pop of colour or metal accents
4. **Consider contrast** - High contrast creates definition

## Neutrals in Different Seasons

### Spring/Summer
- Lighter neutrals (beige, light gray, navy)
- Natural fibers (linen, cotton)
- Softer textures

### Fall/Winter
- Deeper neutrals (charcoal, dark brown, black)
- Heavier fabrics (wool, cashmere)
- Rich textures

## Professional Wardrobe Building

Neutrals are essential for professional wear:
- Invest in quality neutral pieces
- Build around your best neutral
- Use colour as accent, neutrals as base

## Testing Your Neutrals

To find your best neutrals:
1. **Natural light test** - Check colours in daylight
2. **Photography test** - See how colours look on camera
3. **Comfort test** - How do you feel wearing it?
4. **Compliment test** - Do others notice you look great?

## Common Mistakes

1. **Wearing only neutrals** - Too safe, add some colour
2. **Wrong temperature** - Warm vs cool neutrals matter
3. **Too much contrast** - Balance is key
4. **Ignoring undertones** - Your neutrals must match your colouring

## Conclusion

Your neutrals are the foundation of a great wardrobe. Understanding which neutrals work for your specific colouring will transform how you build outfits. Start with your best neutral and build from there - it's the secret to effortless style!`,
    author: 'Emily Watson',
    publishDate: '2024-01-08',
    readTime: 7,
    category: 'Colour Theory',
    tags: ['neutrals', 'wardrobe', 'basics', 'style'],
    image:
      'https://images.unsplash.com/photo-1539109136881-3be0616acf4b?w=800&h=400&fit=crop',
    featured: false,
    relatedColors: ['#2F4F4F', '#696969', '#A9A9A9'],
  },
  {
    id: 'seasonal-color-transitions',
    title: 'Transitioning Your Palette Through Seasons',
    excerpt:
      'Learn how to adapt your colour palette as seasons change while staying true to your core colouring.',
    content: `# Transitioning Your Palette Through Seasons

Your colour palette is constant, but how you use it can change with the seasons. Here's how to adapt.

## The Foundation

Your core colour palette doesn't change with seasons - it's based on your skin tone. However, the intensity, depth, and combinations you use can vary.

## Spring Palette Transition

Spring brings lighter, brighter natural light. This is the time to:
- Use lighter versions of your colours
- Add fresh, clear tones
- Incorporate lighter neutrals
- Build outfits around lighter bases

## Summer Palette Transition

Summer's bright, intense light calls for:
- Your clearest, brightest colours
- High-contrast combinations
- Fresh, light neutrals
- Breathable fabrics in your palette

## Fall Palette Transition

As light softens, transition to:
- Deeper versions of your colours
- Warmer, richer tones
- Medium-weight neutrals
- Layered combinations

## Winter Palette Transition

Winter's lower light levels mean:
- Deepest versions of your colours
- Rich, saturated tones
- Dark neutrals as base
- Heavier, structured fabrics

## Practical Application

Each season, review your palette and identify:
1. Which colours feel most "in season"
2. How to lighten or deepen your palette
3. New combinations to try
4. What to store until next season

Remember: Your palette stays constant, but your application adapts!`,
    author: 'Sarah Chen',
    publishDate: '2024-01-05',
    readTime: 5,
    category: 'Style Guide',
    tags: ['seasons', 'palette', 'wardrobe', 'seasonal'],
    image:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=400&fit=crop',
    featured: false,
  },
  {
    id: 'how-to-find-your-perfect-color-palette',
    title: 'How to Find Your Perfect Colour Palette?',
    excerpt:
      'Discover which colours suit your skin tone best with our comprehensive colour analysis guide. Learn about seasonal colour palettes and skin undertones.',
    content: `# How to Find Your Perfect Colour Palette?

Discover which colours suit your skin tone best with our comprehensive colour analysis guide. Learn about seasonal colour palettes and skin undertones.

## What is Colour Analysis?

Colour analysis is a method used to determine which colours best complement an individual's natural colouring. By analyzing your skin tone, hair colour, and eye colour, colour analysis helps identify the palette of colours that enhance your natural beauty, making you look healthier, more vibrant, and put-together.

When you wear colours that harmonize with your natural colouring, your skin appears clearer, your eyes brighter, and your overall appearance more balanced. On the contrary, wearing colours that clash with your natural palette can make you look tired, washed out, or even sickly.

## The Seasonal Colour Theory

The most popular approach to colour analysis is the seasonal colour theory, which categorizes individuals into four main "seasons": Spring, Summer, Autumn, and Winter. Each season has its own characteristic palette based on temperature (warm or cool) and saturation (bright or muted).

## The Four Seasons Explained

### Spring

**1. Characteristics:** Warm and bright colours with golden undertones
**2. Natural Colouring:** Golden blonde or light auburn hair, warm ivory or peachy skin, blue, green, or hazel eyes
**3. Best Colours:** Warm yellows, coral, peach, light greens, warm blues, golden browns
**4. Colours to Avoid:** Dark, muted, or cool colours like navy, burgundy, gray

### Summer

**1. Characteristics:** Cool and muted colours with blue undertones
**2. Natural Colouring:** Ash blonde, light brown, or gray hair, pink or rosy undertones in skin, blue, gray, or soft brown eyes
**3. Best Colours:** Soft pinks, lavenders, powder blues, sage greens, grays
**4. Colours to Avoid:** Bright, warm colours like orange, bright yellow, tomato red

### Autumn

**1. Characteristics:** Warm and muted colours with earthy undertones
**2. Natural Colouring:** Auburn, copper, or dark warm brown hair, golden or olive skin, green, hazel, or brown eyes
**3. Best Colours:** Terracotta, olive green, rust, mustard, warm browns, forest green
**4. Colours to Avoid:** Cool, bright colours like hot pink, electric blue, icy pastels

### Winter

**1. Characteristics:** Cool and bright colours with blue undertones
**2. Natural Colouring:** Black, dark brown, or white hair, cool ivory or olive skin, dark brown, black, or bright blue eyes
**3. Best Colours:** Black, pure white, royal blue, emerald green, crimson, magenta
**4. Colours to Avoid:** Muted, warm colours like beige, orange-reds, most earth tones

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 8,
    category: 'Colour Theory',
    tags: ['color-analysis', 'seasonal-colors', 'skin-tone', 'color-palette'],
    image: require('../../assets/blog/find_perfect_palette.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3'],
  },
  {
    id: '12-sub-seasons-color-analysis',
    title: 'Did you know that there are 12-Sub Seasons?',
    excerpt:
      'As colour analysis evolved, the seasonal system expanded to include 12 sub-seasons, providing a more nuanced approach to finding your best colours.',
    content: `# Did you know that there are 12-Sub Seasons?

As colour analysis evolved, the seasonal system expanded to include 12 sub-seasons, providing a more nuanced approach to finding your best colours. Each of the four main seasons is divided into three sub-seasons:

## Spring Family

1. Light Spring: The lightest, brightest, and slightly cool-leaning spring palette

2. True/Warm Spring: The warmest and most vibrant spring palette

3. Bright Spring: A high-contrast blend of spring's warmth with some winter's brightness

## Summer Family

1. Light Summer: A light, delicate palette with some spring influence

2. True/Cool Summer: The coolest, most muted summer palette

3. Soft Summer: A muted summer palette with some autumn influence

## Autumn Family

1. Soft Autumn: A muted autumn palette with some summer softness

2. True/Warm Autumn: The warmest, most earthy autumn palette

3. Dark Autumn: A deep autumn palette with some winter darkness

## Winter Family

1. Dark Winter: A deep winter palette with some autumn influence

2. True/Cool Winter: The coolest, most contrasted winter palette

3. Bright Winter: A bright winter palette with some spring's clear brightness

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 5,
    category: 'Colour Theory',
    tags: ['sub-seasons', 'color-analysis', 'seasonal-colors', 'color-palette'],
    image: require('../../assets/blog/12_sub_seasons.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3'],
  },
  {
    id: 'real-life-benefits-color-analysis',
    title: 'Real-life Benefits of Colour Analysis You Never Knew',
    excerpt:
      'Understanding your personal colour palette offers numerous advantages from enhanced appearance to simplified shopping and a more versatile wardrobe.',
    content: `# Real-life Benefits of Colour Analysis You Never Knew

Understanding your personal colour palette offers numerous advantages:

1. Enhanced appearance by wearing your best colours highlights your natural beauty and creates harmony.

2. Simplified shopping because having a defined colour palette makes clothing and makeup purchases more strategic and less overwhelming.

3. Versatile wardrobe with using colours within your palette to naturally coordinate with each other, hence, creating more outfit combinations.

4. Reduced waste by buying colours that truly flatter you leads to fewer unworn items and less waste.

When you look your best, you feel your best and project greater confidence.

## Embracing Your Perfect Palette

Colour analysis is not about limiting your choices, but about empowering you with knowledge to make informed decisions about the colours you wear. While seasonal colour theory provides a helpful framework, remember that it's a guide, not a rulebook.

At ColourSense, we combine traditional colour analysis principles with cutting-edge technology to help you discover your most flattering colours with precision and confidence.

Our app analyzes your unique colouring and provides personalized recommendations that enhance your natural beauty.

Ready to discover your perfect colours? Try ColourSense today and unlock the power of personalized colour recommendations.

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 4,
    category: 'Colour Theory',
    tags: ['color-analysis', 'benefits', 'wardrobe', 'style-tips'],
    image: require('../../assets/blog/benefits_of_color_analysis.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3'],
  },
  {
    id: 'how-to-uncover-your-season-without-overspending',
    title: 'How to Uncover Your Season Without Overspending',
    excerpt:
      'Finding your seasonal colour palette can be done through professional analysis or self-assessment. Discover practical methods to identify your season without breaking the bank.',
    order: 3,
    content: `# How to Uncover Your Season Without Overspending

Finding your seasonal colour palette can be done through professional analysis or self-assessment:

## BUT WANT THE EASY WAY?

Use ColourSense! Our app analyzes your skin tone and provides personalized colour recommendations based on advanced colour science and machine learning.

## EITHER WAY…

**1. Analyze your undertone: **
Look at the veins on your wrist. Blue veins indicate a cool undertone, green veins suggest a warm undertone, and a mix might mean a neutral undertone.

**2. Test with jewelry:**
Notice whether gold (warm) or silver (cool) jewelry complements your skin better.

**3. Consider how your skin reacts to the sun: **
If you tan easily, you might have warm undertones; if you burn easily, you may have cool undertones.

**4. Try colour draping: **
Hold different coloured fabrics near your face in natural light and observe which ones make your skin glow versus those that make you look tired or washed out.

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 4,
    category: 'Style Guide',
    tags: ['color-analysis', 'self-assessment', 'undertones', 'tips'],
    image: require('../../assets/blog/uncover_your_season.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3'],
  },
  {
    id: 'are-you-still-experiencing-makeup-mismatch',
    title: 'Are You Still Experiencing Makeup Mismatch?',
    excerpt:
      'Have you ever swatched a lipstick or dyed your hair only to realize something feels off? Discover how to unlock your ColourSense and choose flattering makeup, lipstick, and hair colours.',
    content: `# Are You Still Experiencing Makeup Mismatch?

Have you ever swatched a lipstick or dyed your hair only to realize something feels... off? The secret lies in your ColourSense, an intuitive understanding of how your skin tone and undertone harmonize with certain shades.

In this blog post, we'll walk you through how to unlock your ColourSense and use it to choose flattering makeup, lipstick, and hair colours that enhance your natural glow.

## 1. Lipstick Shades for Your Undertone

### Cool Undertone

**• Fair Skin:** Soft pinks, berry, plum, rose
**• Medium Skin:** Cranberry, wine, mauve
**• Deep Skin:** Rich raspberry, cherry red, deep burgundy

### Warm Undertone

**• Fair Skin:** Peach, coral, warm nude
**• Medium Skin:** Terracotta, cinnamon, brick red
**• Deep Skin:** Burnt orange, copper, warm brown

## 2. Makeup Base and Blush

### Foundation

**• Cool undertone:** Choose shades labeled "cool," "rosy," or "pink beige."
**• Warm undertone:** Look for "warm," "golden," or "honey."
**• Neutral undertone:** Try "neutral," "ivory," or "buff."

### Blush

**• Cool:** Soft pinks, mauves, plum
**• Warm:** Apricot, peach, coral
**• Neutral:** Dusty rose, warm pinks

## 3. Hair Colour to Match Your ColourSense

### Cool Undertone

**• Best Colours:** Ash brown, platinum blonde, jet black, cool burgundy
**• Avoid:** Warm honey or golden tones that may clash

### Warm Undertone

**• Best Colours:** Golden brown, caramel, warm blonde, copper red
**• Avoid:** Ashy or blue-toned colours

### Neutral Undertone

**• Best Colours:** Rose gold, warm brown, or soft black for a balanced look
**• Flexibility:** You can experiment more freely with both warm and cool shades

Still uncertain? Check ColourSense to discover your most flattering colour match.

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 6,
    category: 'Style Guide',
    tags: ['makeup', 'lipstick', 'hair-color', 'undertones', 'beauty'],
    image: require('../../assets/blog/make_up_mismatch.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#4ECDC4', '#FFE66D', '#95E1D3'],
  },
  {
    id: 'out-of-16-million-colors-which-one-is-truly-you',
    title: 'Out of 16.7 Million Colours, Which One Is Truly You?',
    excerpt:
      'Did you know there are over 16.7 million colour combinations in the standard RGB system? Discover your unique colour story and find the shade that feels like home.',
    content: `# Out of 16.7 Million Colours, Which One Is Truly You?

Did you know there are over 16.7 million colour combinations in the standard RGB system? That is millions of shades, hues, and tones, all coexisting in one beautiful spectrum. And somewhere in that rainbow, there is you.

Maybe you shine like Butter Yellow, soft and sunny. Or maybe you are a Sky Blue, calm but full of quiet energy. You could even be Lavender, mysterious yet effortlessly charming. Every colour tells a story, and yours is waiting to be discovered.

That is the fun part of colour analysis. It is not just about what looks good on you, it is about what feels like you. When you find your perfect colour match, it is like seeing yourself in HD for the first time.

So go ahead, play with palettes, experiment with shades, and let your true colours show. Because out of 16.7 million possibilities, there is one that feels like home. And that is your ColourSense.

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 3,
    category: 'Colour Theory',
    tags: [
      'color-discovery',
      'personal-style',
      'color-palette',
      'self-expression',
    ],
    image: require('../../assets/blog/million_colors.png'),
    featured: true,
    relatedColors: ['#FFD700', '#87CEEB', '#E6E6FA', '#FF6B35'],
  },
  {
    id: 'how-to-improve-your-junk-journaling-with-color-analysis',
    title: 'How to Improve Your Junk Journaling with Colour Analysis',
    excerpt:
      'Junk journaling is all about freedom and creativity. Discover how ColourSense can help you create more cohesive and visually satisfying pages that reflect your personal style.',
    content: `# How to Improve Your Junk Journaling with Colour Analysis

Junk journaling is all about freedom, mixing scraps, memories, and creativity into something that feels completely yours. But have you noticed how some pages feel just right while others seem off? The secret might be in your colour choices, and that is where ColourSense comes in.

ColourSense helps you understand your personal colour palette so you can make your pages more cohesive and visually satisfying. By knowing your colour season—Warm, Cool, or Neutral—you can design spreads that reflect your mood and style.

If you have a Warm palette, try soft browns, golden yellows, or muted oranges for a cozy feel. Cool tones like lavender, periwinkle, or icy blue bring a calm, dreamy vibe. And if you are Neutral, mix both—blush pink with taupe or sage green with soft cream.

## Colour Palettes for Junk Journaling
| Season Type | Mood & Aesthetic | Suggested Colours | Journaling Ideas |
|-------------|------------------|------------------|------------------|
| Warm / Autumn | Cozy, vintage, nostalgic | Burnt orange, caramel brown, mustard yellow, olive green | Use kraft paper, pressed leaves, and sepia-toned photos for a rustic feel. |
| Cool / Winter | Elegant, calm, dreamy | Icy blue, lavender, charcoal gray, deep plum | Combine silver details, translucent vellum, and clean layouts for a frosty aesthetic. |
| Neutral / Spring | Soft, balanced, uplifting | Blush pink, sage green, cream, taupe | Try pastel stickers, soft watercolor backgrounds, and floral prints for a lighthearted vibe. |
| Deep / Summer | Romantic, artistic, expressive | Wine red, navy blue, rosewood, dusty lilac | Layer textures, mix old book pages with lace or fabric, and use handwritten quotes. |

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 5,
    category: 'Style Guide',
    tags: ['junk-journaling', 'color-palette', 'creativity', 'art'],
    image: require('../../assets/blog/junk_journaling.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#8B4513', '#FFD700', '#9370DB'],
  },
  {
    id: 'what-to-wear-on-your-dates-coloursense-edition',
    title: 'What to Wear on Your Dates: The ColourSense Edition',
    excerpt:
      "Dates aren't just about romance — they're about self-expression. Discover how to dress for every kind of date with colour tips that bring out your natural glow.",
    order: 1,
    content: `# What to Wear on Your Dates: The ColourSense Edition

Dates aren't just about romance — they're about self-expression. Whether it's coffee or candlelight, your outfit should feel as good as it looks. Here's how to dress for every kind of date, with colour tips that bring out your natural glow.

## 1. Coffee Date – Effortlessly Charming

Keep it casual but polished. Think comfy knits, flowy skirts, or a favorite pair of jeans.

**• Warm tones:** Go for beige, terracotta, or soft olive.
**• Cool tones:** Try dusty blue, mauve, or silver-gray.
**• Neutral tones:** Blend soft taupe or blush for that calm, cozy energy.

**Style Tip:** Add gold or silver hoops depending on your undertone — it's subtle but powerful.

## 2. Movie Date

It's all about comfort. Layers make you look effortlessly cute (and practical for cold cinemas).

**• Warm tones:** Burnt orange, camel, or honey yellow.
**• Cool tones:** Navy, lavender, or charcoal gray.
**• Neutral tones:** Cream, sand, or blush pink.

**Style Tip:** Pair a simple top with a cardigan and sneakers. You'll look relaxed, not rushed.

## 3. Dinner Date

This is your moment to shine. Choose something that feels elevated but still "you."

**• Warm tones:** Emerald green, rust, or deep coral.
**• Cool tones:** Burgundy, midnight blue, or plum.
**• Neutral tones:** Champagne, rose gold, or ivory.

**Style Tip:** Add a lip shade that matches your undertone — coral for warm, berry for cool, nude rose for neutral.

## 4. Outdoor Date

Stay fresh and comfortable while embracing a natural look.

**• Warm tones:** Mustard, warm white, or sage green.
**• Cool tones:** Sky blue, lilac, or icy pink.
**• Neutral tones:** Peach beige or soft tan.

**Style Tip:** Accessorize with a sun hat or dainty sunglasses for a carefree vibe.

## 5. Art Gallery or Museum Date

Channel your creative side with balanced colours and interesting textures.

**• Warm tones:** Caramel, copper, or bronze.
**• Cool tones:** Steel blue, black, or cool violet.
**• Neutral tones:** Dove gray, taupe, or soft ivory.

**Style Tip:** Mix fabrics like satin, denim, linen for a look that's both thoughtful and original.

Every outfit tells a story, and the best one is the story of you. When your colours complement your undertone, you radiate confidence effortlessly. So the next time you get ready for a date, let your ColourSense lead the way and let your true colours do the flirting.

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 7,
    category: 'Style Guide',
    tags: ['dating', 'outfit-ideas', 'color-palette', 'style-tips'],
    image: require('../../assets/blog/what_to_wear.png'),
    featured: true,
    relatedColors: ['#FF6B35', '#8B4513', '#9370DB', '#FFD700'],
  },
  {
    id: 'color-confidence-guide-for-students',
    title: 'The Much-Needed Guide to Colour Confidence for Students',
    excerpt:
      'School outfits can be tricky. Discover how to use colour analysis to make your everyday school looks feel more put-together and uniquely you.',
    order: 2,
    content: `# The Much-Needed Guide to Colour Confidence for Students

School outfits can be tricky. You want to look put-together but still comfortable, expressive but within the dress code. The secret to nailing that balance? Colour Analysis with ColourSense.

Here's how to use colour analysis to make your everyday school looks feel more put-together and uniquely you.

| Tip | What It Means | How to Apply It | Best Colours |
|-----|---------------|-----------------|-------------|
| 1. Match Your Mood (and Schedule) | Colours influence how you feel and perform during the day. | Coordinate your outfit based on your energy or tasks for the day. | Monday: Calming blue or green<br>Exam days: Focus-boosting navy or red<br>Friday: Playful yellow or coral |
| 2. Layer Smart with Neutrals | Neutrals are the foundation of a cohesive wardrobe. | Choose neutrals that match your undertone for jackets, shoes, or bags. | Warm: Cream, tan, camel<br>Cool: Gray, black, white<br>Neutral: Taupe, sand, oatmeal |
| 3. Express Yourself Through Details | Even with uniform rules, you can still show personality. | Use small coloured accents like scrunchies, socks, stationery, or jewelry. | Pick accent colours from your personal palette to add harmony and fun. |
| 4. Build a Capsule School Wardrobe | Fewer pieces, more combinations. | Stick to 5–7 colours that mix easily and flatter you. | Rotate flattering tones throughout the week for effortless style. |

*Crafted by AI, styled by us — just the way you like it.`,
    author: '',
    publishDate: '2025-12-18',
    readTime: 5,
    category: 'Style Guide',
    tags: ['students', 'school-outfits', 'color-palette', 'wardrobe'],
    image: require('../../assets/blog/much_needed_guide.png'),
    featured: true,
    relatedColors: ['#4169E1', '#FF6B35', '#FFD700', '#9370DB'],
  },
];

export const getArticleById = (id: string): BlogArticle | undefined => {
  return blogArticles.find((article) => article.id === id);
};

export const getFeaturedArticles = (): BlogArticle[] => {
  return blogArticles
    .filter((article) => article.featured)
    .sort((a, b) => {
      // Sort by order property if it exists, otherwise maintain original order
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      return 0;
    });
};

export const getArticlesByCategory = (category: string): BlogArticle[] => {
  return blogArticles.filter((article) => article.category === category);
};

export const getRelatedArticles = (
  articleId: string,
  limit: number = 3
): BlogArticle[] => {
  const article = getArticleById(articleId);
  if (!article) return [];

  return blogArticles
    .filter(
      (a) =>
        a.id !== articleId &&
        (a.category === article.category ||
          a.tags.some((tag) => article.tags.includes(tag)))
    )
    .slice(0, limit);
};
